import { useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import { Box, Text, useToggle } from '@factorialco/deprecated-design-system'
import _ from 'lodash'

import { observer } from 'mobx-react'

import BoxList from 'modules/core/components/BoxList'
import Loading from 'modules/core/components/Loading'
import type MultiFilterForm from 'modules/core/components/MultiFilters/Forms/LogicOperatorForm'
import ScrollableList from 'modules/core/components/ScrollableList'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import type Employee from 'modules/employees/store/models/Employee'
import type SupplementsFormStore from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/store'
import { EmployeeEstimatedPeriodBaseSalary } from 'modules/payroll_bundle/hooks/useEmployeeEstimatedPeriodBaseSalary'
import { RouteContextParams } from 'modules/payroll_bundle/lib/RouteContextParams'
import { TRANSITION_DURATION_FASTER } from 'modules/payroll_bundle/lib/constants'
import { PayrollStatus } from 'modules/payroll_bundle/lib/enums'
import { useStatusAuthorizations } from 'modules/payroll_bundle/lib/statusAuthorizationsContext'

import Header from '../Header'
import CollaborateWidget from './CollaborateWidget'
import SearchBar from './SearchBar'

export interface Params {
  key?: string
  employee: Employee
  estimatedSalary?: EmployeeEstimatedPeriodBaseSalary
  isLast: boolean
  employeeExpansionState?: Record<string, boolean>
  toggleEmployeeExpansion?: (id: string) => void
}

type ItemProps = Params & React.ConsumerProps<Params>

const Item = ({
  employee,
  estimatedSalary,
  isLast,
  children,
  employeeExpansionState,
  toggleEmployeeExpansion,
}: ItemProps) => (
  <>
    {children({
      key: String(employee.attributes.id),
      employee,
      estimatedSalary,
      isLast,
      employeeExpansionState,
      toggleEmployeeExpansion,
    })}
  </>
)

interface Props {
  canChangeStatus?: boolean
  status?: PayrollStatus
  tableElements: Employee[]
  onSearch: (event: React.ChangeEvent<HTMLInputElement>) => void
  search?: string
  loadingEmployees?: boolean
  estimatedEmployeesSalaries: Record<number, EmployeeEstimatedPeriodBaseSalary>
  filteredEmployeesModels: Employee[]
  logicOperator: MultiFilterForm
  filters: MultiFilterForm[]
  onApplyFilters: (
    models: Employee[],
    defaultFilter?: MultiFilterForm[] | undefined
  ) => Promise<void>
  onCancel: () => void
  onRemoveFilter: (index: number) => void
  onAddFilter: () => void
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>
  supplementsStore: SupplementsFormStore
}

const TableV2 = ({
  canChangeStatus,
  tableElements,
  supplementsStore,
  loadingEmployees = false,
  search = '',
  status,
  onSearch,
  children,
  estimatedEmployeesSalaries,
  filteredEmployeesModels,
  logicOperator,
  filters,
  onApplyFilters,
  onCancel,
  onRemoveFilter,
  onAddFilter,
  setCurrentPage,
}: Props & React.ConsumerProps<Params>) => {
  const { filteredEmployees: employeesInPeriod } = useOutletContext<RouteContextParams>()
  const [supplementsExpanded, toggleSupplementsExpanded] = useToggle(false)

  const [employeeExpansionState, setEmployeeExpansionState] = useState<Record<string, boolean>>({})

  const employeeIds = filteredEmployeesModels.map((employee) => employee.attributes.id).join(',')

  const { loading: loadingPolicyPeriodAuthorizations, supplementPermissions } =
    useStatusAuthorizations()
  const canEditSupplements =
    !loadingPolicyPeriodAuthorizations &&
    _.values(supplementPermissions).some((policy) => policy.canEditEmployeeSupplements)

  useUnsafeEffect(() => {
    const defaultSExpansionState: Record<string, boolean> = {}
    filteredEmployeesModels.forEach((employee: Employee) => {
      defaultSExpansionState[employee.attributes.id] = false
    })
    setEmployeeExpansionState(defaultSExpansionState)
  }, [employeeIds]) // eslint-disable-line react-hooks/exhaustive-deps

  const toggleEmployeeExpansion = (id: string) => {
    setEmployeeExpansionState((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }))
  }

  const toggleAllEmployeeExpansionStates = () => {
    toggleSupplementsExpanded(!supplementsExpanded)()
    setEmployeeExpansionState((prevState) => {
      const toggledStateObject: Record<string, boolean> = {}
      for (const key of Object.keys(prevState)) {
        toggledStateObject[key] = !supplementsExpanded
      }
      return toggledStateObject
    })
  }

  return (
    <Box width="full" height="full" gap="s8">
      {(canEditSupplements || canChangeStatus) && (
        <Box width="full" justifyContent="spaceBetween" flexDirection="row">
          <CollaborateWidget status={status} readonly={!canChangeStatus} />
          <Box>{/* TODO: Migrate the table controls here in FCT-7122 */}</Box>
        </Box>
      )}
      <BoxList>
        <Box
          flexDirection="row"
          alignItems="center"
          justifyContent="spaceBetween"
          width="full"
          background="grey200"
          padding="s16"
        >
          <SearchBar
            search={search}
            onSearch={onSearch}
            onAddFilter={onAddFilter}
            onRemoveFilter={onRemoveFilter}
            onApplyFilters={() => {
              onApplyFilters(employeesInPeriod)
              setCurrentPage(1)
            }}
            onCancel={onCancel}
            filters={filters}
            logicOperator={logicOperator}
          />
        </Box>
        <Header
          toggleSupplements={toggleAllEmployeeExpansionStates}
          supplementsExpanded={supplementsExpanded}
          supplementsStore={supplementsStore}
          loadingEmployees={loadingEmployees}
        />
        {loadingEmployees && <Loading />}
        {!loadingEmployees && !tableElements.length && (
          <Box padding="s24" width="full" textAlign="center">
            <Text.Normal color="grey600" weight="medium">
              {i18n.t('employees.no_employees_found')}
            </Text.Normal>
          </Box>
        )}
        {!loadingEmployees && tableElements.length && (
          <ScrollableList size="full">
            {!!tableElements.length &&
              tableElements.map((employee, index) => (
                <Box.Motion
                  key={`employee-supplements-${employee.attributes.id}`}
                  initial={{ translateY: '20px', opacity: 0 }}
                  animate={{ translateY: '0', opacity: 1.0 }}
                  transition={{
                    delay: index * 0.05,
                    type: 'tween',
                    duration: TRANSITION_DURATION_FASTER,
                    ease: 'easeOut',
                  }}
                >
                  <Item
                    employee={employee}
                    estimatedSalary={estimatedEmployeesSalaries[employee.id]}
                    isLast={index === tableElements.length - 1}
                    employeeExpansionState={employeeExpansionState}
                    toggleEmployeeExpansion={toggleEmployeeExpansion}
                  >
                    {children}
                  </Item>
                </Box.Motion>
              ))}
          </ScrollableList>
        )}
      </BoxList>
    </Box>
  )
}

export default observer(TableV2)
